import { Center, Grid, Loader, Select, Stack, Button, Text } from "@mantine/core";
import { useContext, useState } from "react";
import { useFetch } from "@mantine/hooks";
import moment from "moment/moment";
import AuthContext from "../../../contexts/AuthContext";

// Componentes reutilizados de SalesDashboardAcademicUnit
import FunnelKPICards from "../../../components/SalesDashboardAcademicUnit/FunnelKPICards";
import ObjectivesTracker from "../../../components/SalesDashboardAcademicUnit/ObjectivesTracker";
import SellerPerformanceTable from "../../../components/SalesDashboardAcademicUnit/SellerPerformanceTable";
import LeadSourceChart from "../../../components/SalesDashboardAcademicUnit/LeadSourceChart";
import CountryBreakdown from "../../../components/SalesDashboardAcademicUnit/CountryBreakdown";

// Componentes nuevos de Dirección
import MonthlyEvolutionChart from "../../../components/MonthlyEvolutionChart/MonthlyEvolutionChart";
import axios from "axios";

const MONTHS = [
  "ENERO", "FEBRERO", "MARZO", "ABRIL", "MAYO", "JUNIO",
  "JULIO", "AGOSTO", "SEPTIEMBRE", "OCTUBRE", "NOVIEMBRE", "DICIEMBRE",
];

export default function DirectionHome() {
  const { authToken } = useContext(AuthContext);
  const [isGenerating, setIsGenerating] = useState(false);

  const [filters, setFilters] = useState({
    year: moment().year().toString(),
    month: moment().month().toString(),
  });

  // URL del endpoint de dirección
  const dashboardUrl = (() => {
    const base = `${process.env.REACT_APP_BACKEND_SERVER}admin/direccion/dashboard`;
    const params = new URLSearchParams();
    if (filters.year) params.set("year", filters.year);
    if (filters.month !== "") params.set("month", filters.month);
    return `${base}?${params.toString()}`;
  })();

  const config = {
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${authToken}`,
    },
  };

  const { data, loading } = useFetch(dashboardUrl, config);

  if (loading) {
    return (
      <Center py="xl">
        <Stack align="center">
          <Loader />
          <Text>Cargando dashboard estratégico...</Text>
        </Stack>
      </Center>
    );
  }

  if (!data) {
    return (
      <Center py="xl">
        <Text c="dimmed">No se pudieron cargar los datos. Verificá tus permisos.</Text>
      </Center>
    );
  }

  const generateSalesReport = async () => {
    setIsGenerating(true)
    try {
      const URL = `${process.env.REACT_APP_BACKEND_SERVER}admin/direccion/sales-report?year=2026`;
      const config = {
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
        responseType: 'blob', // ← clave: trata la respuesta como binario
      };

      const { data } = await axios.get(URL, config);

      // Crear un enlace temporal y disparar la descarga
      const blobUrl = window.URL.createObjectURL(new Blob([data]));
      const link = document.createElement('a');
      link.href = blobUrl;
      link.setAttribute('download', 'sales-report-2026.xlsx');
      document.body.appendChild(link);
      link.click();

      // Limpiar
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error('Error al generar el reporte:', error);
    } finally {
      setIsGenerating(false)

    }
  };

  return (
    <Stack gap="lg">
      {/* Filtros */}
      <Grid>
        <Grid.Col span={{ base: 6, sm: 3 }}>
          <Select
            label="Año"
            allowDeselect={false}
            value={filters.year}
            onChange={(value) => {
              if (!value) return;
              setFilters((prev) => ({ ...prev, year: value }));
            }}
            data={[
              moment().year().toString(),
              (moment().year() - 1).toString(),
            ]}
          />
        </Grid.Col>
        <Grid.Col span={{ base: 6, sm: 3 }}>
          <Select
            label="Mes"
            allowDeselect={false}
            value={filters.month}
            onChange={(value) => {
              setFilters((prev) => ({ ...prev, month: value }));
            }}
            data={[
              { value: "", label: "Todos" },
              ...MONTHS.map((m, idx) => ({ value: idx.toString(), label: m })),
            ]}
          />
        </Grid.Col>
      </Grid>

      <Button onClick={generateSalesReport} loading={isGenerating} type='button'>Generar reporte de ventas</Button>

      {/* KPIs globales */}
      <FunnelKPICards kpis={data.kpis} />

      {/* Objetivos de inscripción */}
      <ObjectivesTracker objectives={data.objectives} />

      {/* Evolución mensual */}
      <MonthlyEvolutionChart monthlyEvolution={data.monthlyEvolution} />

      {/* Performance por vendedor + Efectividad por fuente */}
      <Grid>
        <Grid.Col span={{ base: 12, md: 7 }}>
          <SellerPerformanceTable sellers={data.bySeller} />
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 5 }}>
          <LeadSourceChart sources={data.bySource} />
        </Grid.Col>
      </Grid>

      {/* Distribución por país */}
      <CountryBreakdown countries={data.byCountry} />
    </Stack>
  );
}
